import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MEMORY_WALL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export default function MemoryWall() {
  const [items, setItems] = useState(MEMORY_WALL);
  const [form, setForm] = useState({ name: "", location: "", remembering: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/tributes?limit=12`)
      .then((r) => {
        if (cancelled) return;
        if (Array.isArray(r.data) && r.data.length > 0) {
          const remote = r.data.map((t) => ({
            name: t.location ? `${t.name}, ${t.location}` : t.name,
            remembering: t.remembering || "In memory",
            message: t.message,
          }));
          setItems([...remote, ...MEMORY_WALL].slice(0, 12));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please share a name and a memory.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tributes`, {
        name: form.name.trim(),
        location: form.location.trim() || null,
        remembering: form.remembering.trim() || null,
        message: form.message.trim(),
      });
      setItems((prev) => [
        {
          name: data.location ? `${data.name}, ${data.location}` : data.name,
          remembering: data.remembering || "In memory",
          message: data.message,
        },
        ...prev,
      ].slice(0, 12));
      setForm({ name: "", location: "", remembering: "", message: "" });
      toast.success("Candle lit. Your memory has been added to the wall.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="memory-wall-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
        <div className="col-span-12 md:col-span-5">
          <div className="overline text-[color:var(--ink-soft)] mb-4">§ The Memory Wall</div>
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(38px, 5vw, 76px)" }}
          >
            Light a candle. <span className="font-italic-serif italic">Leave a line.</span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-7 md:pl-16">
          <p className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)] max-w-lg">
            A public wall where anyone can leave a short remembrance — a name,
            a place, a sentence that keeps someone close. Every candle stays.
          </p>

          <form onSubmit={submit} className="mt-8 grid grid-cols-12 gap-3" data-testid="tribute-form">
            <input
              className="col-span-6 md:col-span-3 bg-transparent border border-[color:var(--ink-line-strong)] px-4 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)]"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="tribute-name"
              maxLength={80}
            />
            <input
              className="col-span-6 md:col-span-3 bg-transparent border border-[color:var(--ink-line-strong)] px-4 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)]"
              placeholder="Your city"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              data-testid="tribute-location"
              maxLength={80}
            />
            <input
              className="col-span-12 md:col-span-6 bg-transparent border border-[color:var(--ink-line-strong)] px-4 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)]"
              placeholder="Remembering (e.g. Grandma — Eleanor Hayes)"
              value={form.remembering}
              onChange={(e) => setForm({ ...form, remembering: e.target.value })}
              data-testid="tribute-remembering"
              maxLength={80}
            />
            <textarea
              className="col-span-12 bg-transparent border border-[color:var(--ink-line-strong)] px-4 py-3 text-[14px] focus:outline-none focus:border-[color:var(--ink)] resize-none"
              placeholder="A line, a memory, a thank-you…"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="tribute-message"
              maxLength={280}
            />
            <div className="col-span-12 flex items-center justify-between">
              <span className="numeric-index text-[color:var(--ink-soft)]">
                {form.message.length}/280 · Held in the archive
              </span>
              <button
                type="submit"
                disabled={loading}
                className="btn-rust disabled:opacity-60"
                data-testid="tribute-submit"
              >
                {loading ? "Lighting…" : "Light a candle"}
                <span aria-hidden>✦</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Wall grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[color:var(--ink-line)]">
        {items.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease }}
            className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 relative bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
            style={{ transition: "background-color 500ms ease" }}
            data-testid={`tribute-item-${i}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[color:var(--rust)] text-lg">✦</span>
              <span className="numeric-index text-[color:var(--ink-soft)]">
                {String(i + 1).padStart(3, "0")}
              </span>
            </div>
            <p
              className="font-italic-serif italic leading-[1.35] text-[color:var(--ink)]"
              style={{ fontSize: "clamp(20px, 1.8vw, 26px)" }}
            >
              "{m.message}"
            </p>
            <div className="mt-6 pt-4 border-t border-[color:var(--ink-line)] flex items-center justify-between">
              <div>
                <div className="text-[13px] font-medium">{m.name}</div>
                <div className="numeric-index text-[color:var(--ink-soft)] mt-1">
                  {m.remembering}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
